import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Package, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ForgotPassword } from "@/components/ForgotPassword";

const BG = "https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwd2FyZWhvdXNlJTIwbG9naXN0aWNzfGVufDB8fHx8MTc4NTYxMzQ3N3ww&ixlib=rb-4.1.0&q=85";

const roleHome = { warehouse_agent: "/warehouse", front_desk_agent: "/frontdesk", supervisor: "/supervisor", company_admin: "/admin", platform_admin: "/platform" };

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome, ${res.user.full_name}`);
      navigate(roleHome[res.user.role] || "/");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BG})` }} />
      <div className="absolute inset-0 bg-black/85" />
      <div className="relative z-10 w-full max-w-md bg-card/90 backdrop-blur border border-border p-8">
        <div className="mb-8">
          <div className="bg-white rounded-lg px-5 py-4 flex items-center justify-center">
            <img src="/trackboard-logo.png" alt="Trackboard by LOM Group JA" className="w-64 h-auto" data-testid="login-logo" />
          </div>
        </div>
        {forgot ? (
          <ForgotPassword initialEmail={email} onDone={(em) => { if (em) { setEmail(em); setPassword(""); } setForgot(false); }} />
        ) : (<>
        <h1 className="font-display text-2xl font-bold mb-1">Employee Sign In</h1>
        <p className="text-sm text-muted-foreground mb-6">Access your store dashboard.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">Email</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="mt-1 h-11" placeholder="you@company.com" data-testid="login-email" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">Password</label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              className="mt-1 h-11" placeholder="••••••••" data-testid="login-password" />
            <div className="text-right mt-1">
              <button type="button" onClick={() => setForgot(true)} className="text-xs text-muted-foreground hover:text-white hover:underline" data-testid="forgot-password-link">
                Forgot password?
              </button>
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full h-11 text-base font-semibold" data-testid="login-submit">
            {loading ? <Loader2 className="animate-spin" /> : "Sign In"}
          </Button>
        </form>
        <div className="mt-4 text-center text-sm text-muted-foreground">
          New courier company?{" "}
          <button type="button" onClick={() => navigate("/register")} className="text-white font-semibold hover:underline" data-testid="go-register-link">
            Create your account
          </button>
        </div>
        <div className="mt-2 text-center text-sm">
          <button type="button" onClick={() => navigate("/pricing")} className="text-muted-foreground hover:text-white hover:underline" data-testid="go-pricing-link">
            View pricing
          </button>
        </div>
        </>)}
      </div>
    </div>
  );
}
